/**
 * Admin Agents Management Tab Component
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { agentsAPI, pocAPI, applicationsAPI } from '@/lib/api';
import { 
  Building2, Users, CheckCircle, XCircle, Clock, 
  UserCheck, Loader2, Eye, Search, X, FileText, 
  GraduationCap, TrendingUp, Mail, Phone, Trash2, AlertTriangle, ChevronRight, Pencil, ExternalLink, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_LABEL = {
  national_id: 'ID',
  company_registration: 'Company Reg',
  agreement: 'Agreement',
};

export const AgentsTab = ({ 
  agents, 
  employees, 
  onRefresh, 
  onViewAgent,
  isSuperAdmin 
}) => {
  const navigate = useNavigate();
  const [showPOCDialog, setShowPOCDialog] = useState(false);
  const [selectedAgentForPOC, setSelectedAgentForPOC] = useState(null);
  const [assigningPOC, setAssigningPOC] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Agent Details Dialog state
  const [showAgentDetailsDialog, setShowAgentDetailsDialog] = useState(false);
  const [agentDetails, setAgentDetails] = useState(null);
  const [loadingAgentDetails, setLoadingAgentDetails] = useState(false);
  
  // Delete agent state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Edit agent state
  const [showEditAgent, setShowEditAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [savingAgent, setSavingAgent] = useState(false);

  // Compliance summary (mandatory docs missing per agent) — super-admin only.
  // One bulk call instead of N+1 to keep the agents tab snappy.
  const [complianceMap, setComplianceMap] = useState({});

  // Reminder dialog state (super-admin opens, picks type, sends nudge email)
  const [reminderAgent, setReminderAgent] = useState(null);
  const [reminderType, setReminderType] = useState('both');
  const [reminderMessage, setReminderMessage] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);

  const openReminderDialog = (agent) => {
    setReminderAgent(agent);
    setReminderType('both');
    setReminderMessage('');
  };

  const handleSendReminder = async () => {
    if (!reminderAgent) return;
    setSendingReminder(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/agents/${reminderAgent.id}/reminders/send`,
        { type: reminderType, custom_message: reminderMessage },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      if (data?.sent) {
        toast.success(`Reminder sent to ${data.to}`);
      } else {
        toast(data?.reason || 'Nothing to remind — agent is fully compliant', { description: 'No email was sent.' });
      }
      setReminderAgent(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send reminder';
      toast.error(msg);
    } finally {
      setSendingReminder(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/admin/agents/compliance-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const map = {};
        (r.data?.agents || []).forEach(a => { map[a.id] = a; });
        setComplianceMap(map);
      })
      .catch(() => { /* non-fatal — badges just won't render */ });
  }, [isSuperAdmin, agents.length]);

  // Filter agents based on search query (name, email, company, ID, referral code, phone)
  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return agents;
    const query = searchQuery.toLowerCase().trim();
    return agents.filter(agent => 
      agent.full_name?.toLowerCase().includes(query) ||
      agent.email?.toLowerCase().includes(query) ||
      agent.company_name?.toLowerCase().includes(query) ||
      agent.id?.toLowerCase().includes(query) ||
      agent.user_id?.toLowerCase().includes(query) ||
      agent.referral_code?.toLowerCase().includes(query) ||
      agent.reference_code?.toLowerCase().includes(query) ||
      agent.phone?.toLowerCase().includes(query) ||
      agent.country?.toLowerCase().includes(query) ||
      agent.city?.toLowerCase().includes(query)
    );
  }, [agents, searchQuery]);

  // Filter pending and approved from filtered list
  const pendingAgents = filteredAgents.filter(a => a.approval_status === 'pending');
  const approvedAgents = filteredAgents.filter(a => a.approval_status === 'approved');

  const handleAssignPOC = async (employeeId) => {
    if (!selectedAgentForPOC) return;
    setAssigningPOC(true);
    try {
      await pocAPI.assign(selectedAgentForPOC.id, employeeId);
      toast.success('POC assigned successfully');
      setShowPOCDialog(false);
      setSelectedAgentForPOC(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign POC');
    } finally {
      setAssigningPOC(false);
    }
  };

  const handleViewAgent = (agent) => {
    setSelectedAgent(agent);
    setRejectionReason('');
    if (onViewAgent) onViewAgent(agent);
  };
  
  const handleViewAgentDetails = async (agent) => {
    setLoadingAgentDetails(true);
    setShowAgentDetailsDialog(true);
    try {
      const response = await agentsAPI.getDetails(agent.id);
      setAgentDetails(response.data);
    } catch (error) {
      toast.error('Failed to load agent details');
      setShowAgentDetailsDialog(false);
    } finally {
      setLoadingAgentDetails(false);
    }
  };

  // Auto-open agent detail dialog when arrived via deep link
  // (e.g. /admin?tab=agents&agentId=<id> from the Students tab Source column).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const agentId = params.get('agentId');
    if (!agentId || !agents?.length) return;
    const target = agents.find((a) => a.id === agentId || a.user_id === agentId);
    if (target) {
      handleViewAgentDetails(target);
      // Clean the param so the dialog can be closed without it reopening.
      params.delete('agentId');
      const newQuery = params.toString();
      navigate(`/admin?${newQuery}`, { replace: true });
    } else {
      toast.error('Agent not found (may have been removed)');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents]);

  const handleStatusUpdate = async () => {
    if (!selectedAgent || !pendingAction) return;
    if (pendingAction.status === 'rejected' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setUpdatingStatus(true);
    try {
      await agentsAPI.updateStatus(selectedAgent.id, pendingAction.status, rejectionReason);
      toast.success(`Agent ${pendingAction.status} successfully`);
      setShowStatusDialog(false);
      setPendingAction(null);
      setRejectionReason('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!deletingAgent) return;
    setIsDeleting(true);
    try {
      await agentsAPI.delete(deletingAgent.id);
      toast.success(`Agent "${deletingAgent.company_name || deletingAgent.full_name}" deleted successfully`);
      setShowDeleteConfirm(false);
      setDeletingAgent(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirm = (agent) => {
    setDeletingAgent(agent);
    setShowDeleteConfirm(true);
  };

  const openEditAgent = (agent) => {
    setEditingAgent({
      ...agent,
      phone: agent.phone || '',
      company_name: agent.company_name || '',
      country: agent.country || '',
      city: agent.city || '',
      years_experience: agent.years_experience || 0,
      students_placed: agent.students_placed || 0,
      british_council_certified: agent.british_council_certified || false
    });
    setShowEditAgent(true);
  };

  const handleSaveAgent = async () => {
    if (!editingAgent) return;
    setSavingAgent(true);
    try {
      await agentsAPI.updateProfile(editingAgent.id, {
        full_name: editingAgent.full_name,
        phone: editingAgent.phone,
        company_name: editingAgent.company_name,
        country: editingAgent.country,
        city: editingAgent.city,
        years_experience: parseInt(editingAgent.years_experience) || 0,
        students_placed: parseInt(editingAgent.students_placed) || 0,
        british_council_certified: editingAgent.british_council_certified
      });
      toast.success('Agent profile updated successfully');
      setShowEditAgent(false);
      setEditingAgent(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSavingAgent(false);
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'compliance_check': return <Clock className="w-4 h-4 text-teal-600" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'approved': return 'bg-green-100 text-green-600';
      case 'rejected': return 'bg-red-100 text-red-600';
      case 'compliance_check': return 'bg-teal-100 text-teal-600';
      default: return 'bg-gray-300 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Pending Approvals */}
      {pendingAgents.length > 0 && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            Pending Approvals ({pendingAgents.length})
          </h3>
          <div className="space-y-4">
            {pendingAgents.map((agent) => (
              <div key={agent.id} className="bg-teal-500/10 dark:bg-teal-900/30 border border-teal-300 dark:border-teal-700 rounded-xl p-4" data-testid={`pending-agent-${agent.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <p className="text-gray-800 dark:text-white font-medium">{agent.full_name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{agent.company_name || 'Independent Agent'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {agent.british_council_certified && (
                          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs">BC Certified</Badge>
                        )}
                        <span className="text-gray-400 text-xs">{agent.years_experience} years exp.</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedAgent(agent);
                        setPendingAction({ status: 'approved' });
                        setShowStatusDialog(true);
                      }}
                      className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-500/30"
                      data-testid={`approve-agent-${agent.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedAgent(agent);
                        setPendingAction({ status: 'rejected' });
                        setShowStatusDialog(true);
                      }}
                      className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-500/30"
                      data-testid={`reject-agent-${agent.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Agents */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">All Agents</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredAgents.length === agents.length 
                ? `${agents.length} registered agents` 
                : `Showing ${filteredAgents.length} of ${agents.length} agents`}
            </p>
          </div>
          {/* Search Input + Download */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, company, ID, referral code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 w-full sm:w-80 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
                data-testid="agent-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const { reportsAPI, downloadCSV } = await import('@/lib/api');
                  await downloadCSV(reportsAPI.exportAgents(), `agents_report_${new Date().toISOString().slice(0,10)}.csv`);
                  toast.success('Agents CSV downloaded');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Export failed');
                }
              }}
              className="whitespace-nowrap"
              data-testid="agents-export-csv"
              title="Download agents list as CSV (super admin only)"
            >
              <FileText className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-600">
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Agent</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">POC</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Students</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Commission</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500 dark:text-gray-400">
                    {searchQuery ? `No agents found matching "${searchQuery}"` : 'No agents registered yet'}
                  </td>
                </tr>
              ) : (
              filteredAgents.map((agent) => (
                <tr key={agent.id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50" data-testid={`agent-row-${agent.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-gray-800 dark:text-white font-medium">{agent.full_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{agent.company_name || agent.email}</p>
                        <p className="text-xs text-gray-400 font-mono">{agent.id?.slice(0, 8)}...</p>
                        {isSuperAdmin && complianceMap[agent.id] && complianceMap[agent.id].missing_count > 0 && (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin?tab=system-health&recover=${agent.id}`)}
                            className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 px-2 py-0.5 rounded-full transition-colors"
                            title={`Missing: ${complianceMap[agent.id].missing.map(m => DOC_LABEL[m] || m).join(', ')}. Click to open recovery tool.`}
                            data-testid={`agent-missing-docs-${agent.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Missing {complianceMap[agent.id].missing_count} doc{complianceMap[agent.id].missing_count === 1 ? '' : 's'}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={getStatusBadge(agent.approval_status)}>
                      {getStatusIcon(agent.approval_status)}
                      <span className="ml-1 capitalize">{agent.approval_status}</span>
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    {agent.poc_name ? (
                      <div>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">{agent.poc_name}</p>
                        <p className="text-gray-400 text-xs">{agent.poc_email}</p>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAgentForPOC(agent);
                          setShowPOCDialog(true);
                        }}
                        className="text-xs border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400"
                        data-testid={`assign-poc-${agent.id}`}
                      >
                        <UserCheck className="w-3 h-3 mr-1" />
                        Assign POC
                      </Button>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                      <Users className="w-3 h-3 mr-1" />
                      {agent.students_placed || 0}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-teal-600 dark:text-teal-400 font-medium">
                    £{(agent.commission_earned || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/admin/agents/${agent.id}`)}
                        className="bg-teal-600/10 text-teal-600 hover:bg-teal-600/20 dark:bg-teal-500/20 dark:text-teal-400"
                        data-testid={`view-agent-${agent.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openReminderDialog(agent)}
                          className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                          title="Send reminder (signature / missing docs)"
                          data-testid={`remind-agent-${agent.id}`}
                        >
                          <Bell className="w-4 h-4" />
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditAgent(agent)}
                          className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          data-testid={`edit-agent-${agent.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openDeleteConfirm(agent)}
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          data-testid={`delete-agent-${agent.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Agent Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Agent
            </DialogTitle>
          </DialogHeader>
          {deletingAgent && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Are you sure you want to permanently delete <strong>{deletingAgent.company_name || deletingAgent.full_name}</strong>?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Warning:</strong> The agent applications will be unlinked (not deleted). This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 border-gray-300 dark:border-slate-600 dark:text-gray-300"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteAgent}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeleting}
                  data-testid="confirm-delete-agent"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Agent
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog — super-admin / lead-manager nudges agent about
          missing signature and/or pending mandatory documents */}
      <Dialog open={!!reminderAgent} onOpenChange={(open) => !open && setReminderAgent(null)}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Send reminder
            </DialogTitle>
          </DialogHeader>
          {reminderAgent && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Send a nudge email to <b>{reminderAgent.full_name}</b> at <span className="font-mono">{reminderAgent.email}</span>.
              </p>
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-200">What's pending?</Label>
                <Select value={reminderType} onValueChange={setReminderType}>
                  <SelectTrigger data-testid="reminder-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="signature">Pending signature only</SelectItem>
                    <SelectItem value="missing_docs">Missing documents only</SelectItem>
                    <SelectItem value="both">Both (recommended)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  We'll automatically skip items the agent has already completed — no spam.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-200">Personal note (optional)</Label>
                <Textarea
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                  placeholder="e.g. We're processing your first application this week and need this in place."
                  rows={3}
                  data-testid="reminder-message"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setReminderAgent(null)} disabled={sendingReminder}>Cancel</Button>
                <Button onClick={handleSendReminder} disabled={sendingReminder} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="send-reminder-btn">
                  {sendingReminder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                  Send reminder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={showEditAgent} onOpenChange={setShowEditAgent}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit Agent Profile
            </DialogTitle>
          </DialogHeader>
          {editingAgent && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={editingAgent.full_name || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, full_name: e.target.value })}
                  placeholder="Agent's full name"
                  data-testid="edit-agent-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={editingAgent.email || ''}
                  disabled
                  className="bg-gray-100 dark:bg-slate-700 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editingAgent.phone || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, phone: e.target.value })}
                  placeholder="+92..."
                  data-testid="edit-agent-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={editingAgent.company_name || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, company_name: e.target.value })}
                  placeholder="Company/Agency name"
                  data-testid="edit-agent-company"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={editingAgent.country || ''}
                    onChange={(e) => setEditingAgent({ ...editingAgent, country: e.target.value })}
                    placeholder="e.g., Pakistan"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={editingAgent.city || ''}
                    onChange={(e) => setEditingAgent({ ...editingAgent, city: e.target.value })}
                    placeholder="e.g., Lahore"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input
                    type="number"
                    value={editingAgent.years_experience || 0}
                    onChange={(e) => setEditingAgent({ ...editingAgent, years_experience: e.target.value })}
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Students Placed</Label>
                  <Input
                    type="number"
                    value={editingAgent.students_placed || 0}
                    onChange={(e) => setEditingAgent({ ...editingAgent, students_placed: e.target.value })}
                    min="0"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                <input
                  type="checkbox"
                  id="british-council"
                  checked={editingAgent.british_council_certified || false}
                  onChange={(e) => setEditingAgent({ ...editingAgent, british_council_certified: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="british-council" className="cursor-pointer">British Council Certified</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowEditAgent(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAgent}
                  disabled={savingAgent}
                  className="flex-1 bg-teal-600 text-white hover:bg-teal-700"
                  data-testid="save-agent-btn"
                >
                  {savingAgent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* POC Assignment Dialog */}
      <Dialog open={showPOCDialog} onOpenChange={setShowPOCDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Assign Point of Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-gray-500 text-sm">
              Select an employee to be the POC for <span className="text-gray-800">{selectedAgentForPOC?.full_name}</span>
            </p>
            <div className="space-y-2">
              {employees.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => handleAssignPOC(emp.id)}
                  disabled={assigningPOC}
                  className="w-full p-3 rounded-lg bg-[#e9ecef] hover:bg-gray-200 text-left flex items-center justify-between"
                  data-testid={`poc-option-${emp.id}`}
                >
                  <div>
                    <p className="text-gray-800 font-medium">{emp.full_name}</p>
                    <p className="text-gray-500 text-sm">{emp.email}</p>
                  </div>
                  {assigningPOC ? <Loader2 className="w-4 h-4 animate-spin text-teal-600" /> : null}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              {pendingAction?.status === 'approved' ? 'Approve Agent' : 'Reject Agent'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-gray-500">
              Are you sure you want to {pendingAction?.status} <span className="text-gray-800 font-medium">{selectedAgent?.full_name}</span>?
            </p>
            {pendingAction?.status === 'rejected' && (
              <div className="space-y-2">
                <Label className="text-gray-600">Reason for Rejection *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="bg-[#e9ecef] border-gray-200 min-h-[100px]"
                  placeholder="Please provide a reason..."
                  data-testid="rejection-reason"
                />
              </div>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowStatusDialog(false)}
                className="flex-1 border-gray-300 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStatusUpdate}
                disabled={updatingStatus}
                className={`flex-1 font-semibold ${
                  pendingAction?.status === 'approved' 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
                data-testid="confirm-status-btn"
              >
                {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Details Dialog - Shows students and applications */}
      <Dialog open={showAgentDetailsDialog} onOpenChange={setShowAgentDetailsDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Agent Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingAgentDetails ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
              <p className="text-gray-500 mt-2">Loading agent details...</p>
            </div>
          ) : agentDetails ? (
            <div className="space-y-4 sm:space-y-6 mt-4">
              {/* Agent Info - Responsive */}
              <div className="p-3 sm:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white">{agentDetails.agent?.full_name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{agentDetails.agent?.company_name}</p>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span className="truncate max-w-[150px] sm:max-w-none">{agentDetails.user?.email}</span>
                      </span>
                      {agentDetails.user?.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {agentDetails.user?.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge className={getStatusBadge(agentDetails.agent?.approval_status)}>
                    {agentDetails.agent?.approval_status}
                  </Badge>
                </div>
              </div>

              {/* Stats Cards - Clickable */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div 
                  className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all hover:shadow-md hover:scale-105 border-2 border-transparent hover:border-blue-300"
                  onClick={() => {
                    document.getElementById('agent-students-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  title="Click to view students"
                >
                  <p className="text-xl sm:text-2xl font-semibold text-blue-600 dark:text-blue-400">{agentDetails.stats?.total_students || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Students ↓</p>
                </div>
                <div 
                  className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-all hover:shadow-md hover:scale-105 border-2 border-transparent hover:border-purple-300"
                  onClick={() => {
                    document.getElementById('agent-applications-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  title="Click to view applications"
                >
                  <p className="text-xl sm:text-2xl font-semibold text-purple-600 dark:text-purple-400">{agentDetails.stats?.total_applications || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Applications ↓</p>
                </div>
                <div 
                  className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/40 transition-all hover:shadow-md hover:scale-105 border-2 border-transparent hover:border-green-300"
                  onClick={() => {
                    document.getElementById('agent-applications-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  title="Click to view enrolled applications"
                >
                  <p className="text-xl sm:text-2xl font-semibold text-green-600 dark:text-green-400">{agentDetails.stats?.enrolled || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Enrolled ↓</p>
                </div>
                <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-center">
                  <p className="text-xl sm:text-2xl font-semibold text-teal-600 dark:text-teal-400">{agentDetails.stats?.conversion_rate || 0}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Conversion</p>
                </div>
              </div>

              {/* Students List - Clickable */}
              <div id="agent-students-section">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Students ({agentDetails.students?.length || 0})
                  <span className="text-xs font-normal text-gray-400">(click to view)</span>
                </h4>
                {agentDetails.students?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {agentDetails.students.map((student) => (
                      <div 
                        key={student.id} 
                        className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg flex items-center justify-between cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all border-2 border-transparent hover:border-blue-400 hover:shadow-md group"
                        onClick={() => {
                          window.location.href = `/admin/applications/${student.id}`;
                        }}
                        title="Click to view student applications"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center group-hover:bg-blue-200">
                            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 dark:text-white group-hover:text-blue-600">{student.full_name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{student.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right text-sm">
                            <p className="text-gray-600 dark:text-gray-400">{student.profile?.preferred_countries?.slice(0,2).join(', ') || '-'}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{student.profile?.field_of_study || ''}</p>
                          </div>
                          <TrendingUp className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500 text-sm py-4 text-center">No students yet</p>
                )}
              </div>

              {/* Applications List - Clickable */}
              <div id="agent-applications-section">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Applications ({agentDetails.applications?.length || 0})
                  <span className="text-xs font-normal text-gray-400">(click to view)</span>
                </h4>
                {agentDetails.applications?.length > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {agentDetails.applications.map((app) => (
                      <div 
                        key={app.id} 
                        className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-all border-2 border-transparent hover:border-purple-400 hover:shadow-md group"
                        onClick={() => {
                          window.location.href = `/admin/applications/${app.id}`;
                        }}
                        title="Click to view application details"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mt-1 group-hover:bg-purple-200">
                              <GraduationCap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 dark:text-white group-hover:text-purple-600">{app.student_name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{app.course_name}</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500">{app.university_name}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="text-right">
                              <Badge className={`text-xs ${
                                app.status === 'enrolled' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                                app.status === 'accepted' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                                app.status === 'rejected' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {app.status}
                              </Badge>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(app.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <TrendingUp className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors mt-1" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500 text-sm py-4 text-center">No applications yet</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentsTab;
